import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import {
  EuleGlyph,
  BieneGlyph,
  KatzeGlyph,
  HundGlyph,
} from "@/components/FokustypGlyphs";
import { ArrowRight, RotateCw, Save, Check, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import ShareFokustypButtonV2 from "@/components/ShareFokustypButtonV2";
import { pickProSoloCopy } from "@/lib/proTrialCopy";

const GLYPH = { eule: EuleGlyph, biene: BieneGlyph, katze: KatzeGlyph, hund: HundGlyph };
const NAMES = { eule: "Eule", biene: "Biene", katze: "Katze", hund: "Hund" };

// User-facing-Erklärungen (Master-Prompt v2.5 §4.6)
const PRIMARY_TEXT = {
  eule: {
    intro:
      "Du gehst durch die Welt mit einer inneren Brille, die nach Sinn, Logik und dem großen Bild fragt. Bevor du auf etwas reagierst, beobachtest du erstmal. Du willst verstehen, WARUM etwas wichtig ist, bevor du entscheidest, OB du mitmachst.",
    auszeichnet: [
      "Du erkennst Muster und Inkohärenzen, bevor andere es bemerken.",
      "Du verteidigst eine Position mit klaren Argumenten — und mit Daten.",
      "Du kannst Unbequemes ansprechen, weil du dich nicht an Stimmung orientierst.",
    ],
    fordert: [
      "Wenn dein Gegenüber emotional reagiert, weißt du manchmal nicht, wie du mitschwingen sollst.",
      "Du wirst ungeduldig, wenn Logik ignoriert wird.",
    ],
  },
  biene: {
    intro:
      "Du denkst in Schritten, Reihenfolgen und Systemen. Wenn etwas geplant werden soll, bist du in deinem Element. Du brauchst Klarheit, wie etwas ablaufen soll — sonst fühlt sich der Anfang falsch an.",
    auszeichnet: [
      "Du erklärst komplexe Dinge so, dass andere sie verstehen.",
      "Du hältst Abläufe ein und sorgst dafür, dass nichts vergessen wird.",
      "Du bist die Person, die im Chaos Struktur schafft.",
    ],
    fordert: [
      "Wenn jemand \u201Eeinfach mal machen will\u201C ohne Plan, fühlst du dich unwohl.",
      "Du kannst so im Detail stecken, dass der Überblick verloren geht.",
    ],
  },
  katze: {
    intro:
      "Du gehst durch die Welt mit einer inneren Brille, die nach Freiheit, Eigenwillen und Selbstbestimmung fragt. Du spürst sofort, wenn dich jemand in eine Schublade stecken will. Du entscheidest lieber spontan, als dich vorher festzulegen.",
    auszeichnet: [
      "Du sagst offen, was du denkst — auch wenn es unbequem ist.",
      "Du gehst deinen eigenen Weg, selbst wenn andere es nicht verstehen.",
      "Du brauchst Wahlmöglichkeiten, nicht Anweisungen.",
    ],
    fordert: [
      "Du kannst so eigenwillig wirken, dass andere sich nicht ernst genommen fühlen.",
      "Strukturen und Regeln fühlen sich für dich oft wie Käfige an.",
    ],
  },
  hund: {
    intro:
      "Du gehst durch die Welt mit einer inneren Brille, die nach Beziehung, Sicherheit und Zugehörigkeit fragt. Du spürst sofort, wenn es jemandem in deiner Umgebung nicht gut geht. Du misst Erfolg daran, ob die Menschen um dich herum okay sind.",
    auszeichnet: [
      "Du spürst Stimmungen und Spannungen, bevor andere sie benennen.",
      "Du gibst anderen Sicherheit durch deine Verlässlichkeit.",
      "Du bist die Person, die Konflikte nicht eskalieren lässt.",
    ],
    fordert: [
      "Du nimmst die Gefühle anderer zu sehr auf dich.",
      "Du vermeidest manchmal Kritik, obwohl sie nötig wäre.",
    ],
  },
};

// 3 Magic Tipps je primären Fokustyp (v2.5 §4.7)
const MAGIC_TIPPS = {
  eule: [
    "Fang mit dem Sinn an. „Wir machen das, weil…“ reicht oft schon.",
    "Frage nach dem großen Bild, bevor du in Details gehst.",
    "Gib Kontext, damit die Logik nachvollziehbar wird.",
  ],
  biene: [
    "Gib Anderen die Schritte. „Schritt 1, Schritt 2, Schritt 3“ ist Bienen-Sprache.",
    "Strukturiere deine Aussagen. „Erstens…, zweitens…, drittens…“ hilft Bienen.",
    "Visualisiere Abläufe. Mindmaps, Listen, Diagramme — alles, was Struktur sichtbar macht.",
  ],
  katze: [
    "Gib Anderen Wahlmöglichkeiten. „Möglichkeit A, B oder was ganz anderes“ ist Katzen-Sprache.",
    "Lass Druck raus. „Wenn du Lust hast, kannst du… — kein Stress, wenn nicht.“",
    "Frag nach dem Warum hinter dem Was. Katzen brauchen den Sinn, nicht nur die Anweisung.",
  ],
  hund: [
    "Sag erst „Wir sind okay, du und ich“ — dann kommt der Sachinhalt. Hunde brauchen Sicherheit, bevor sie zuhören können.",
    "Frag nach den Menschen. „Was bedeutet das für [Person]?“ aktiviert den Hund-Fokus.",
    "Anerkennung vor Kritik. „Du machst das gut. Eine Sache, die wir anders machen könnten…“ ist Hund-Sprache.",
  ],
};

// Quadranten-Positionen für die 2x2-Matrix
// X-Achse: links = sachlich (Eule/Biene), rechts = intrinsisch (Katze/Hund)
// Y-Achse: oben = WOLLEN (Eule/Katze), unten = KÖNNEN (Biene/Hund)
const MATRIX_QUADRANTS = [
  { slug: "eule", label: "Eule", x: 25, y: 25 },
  { slug: "katze", label: "Katze", x: 75, y: 25 },
  { slug: "biene", label: "Biene", x: 25, y: 75 },
  { slug: "hund", label: "Hund", x: 75, y: 75 },
];

function MatrixVisualizer({ matrixPosition, primary }) {
  // matrixPosition.x, .y aus Backend: Range -100..+100
  // Map auf 0..100 für CSS (left/top in %)
  const px = Math.min(100, Math.max(0, 50 + matrixPosition.x / 4));
  const py = Math.min(100, Math.max(0, 50 - matrixPosition.y / 4));
  const PrimaryGlyph = GLYPH[primary];
  return (
    <div
      className="relative aspect-square w-full max-w-md mx-auto rounded-2xl border border-[var(--fts-neutral-200)] bg-gradient-to-br from-[var(--fts-neutral-50)] to-white p-4"
      data-testid="result-matrix"
    >
      {/* Achsenkreuz */}
      <div className="absolute left-1/2 top-4 bottom-4 w-px bg-slate-200" />
      <div className="absolute top-1/2 left-4 right-4 h-px bg-slate-200" />
      {/* Achsenlabels — Sprint 3a: alltagsnah */}
      <span
        className="absolute top-2 left-1/2 -translate-x-1/2 text-[10px] font-bold tracking-wide text-[var(--fts-text-muted)]"
        data-testid="matrix-label-top"
      >
        Was beschäftigt dich?
      </span>
      <span
        className="absolute bottom-2 left-1/2 -translate-x-1/2 text-[10px] font-bold tracking-wide text-[var(--fts-text-muted)]"
        data-testid="matrix-label-bottom"
      >
        Was funktioniert?
      </span>
      <span
        className="absolute left-2 top-1/2 -translate-y-1/2 -rotate-90 text-[10px] font-bold tracking-wide text-[var(--fts-text-muted)] origin-center"
        data-testid="matrix-label-left"
      >
        Sache
      </span>
      <span
        className="absolute right-2 top-1/2 -translate-y-1/2 rotate-90 text-[10px] font-bold tracking-wide text-[var(--fts-text-muted)] origin-center"
        data-testid="matrix-label-right"
      >
        Mensch
      </span>

      {/* 4 Quadrant-Glyphen mit Sprint 4c-3 C.1b Role-Tints (Primary/Gegenüber/Nachbarn) */}
      {MATRIX_QUADRANTS.map(({ slug, label, x, y }) => {
        const Glyph = GLYPH[slug];
        const isPrimary = slug === primary;
        // Role-Mapping: eule↔hund, biene↔katze (diagonal entgegengesetzt).
        const DIAG = { eule: "hund", biene: "katze", katze: "biene", hund: "eule" };
        let roleBg = "#FEF3C7"; // Nachbarn default (Amber-Tint)
        if (isPrimary) roleBg = "#DCFCE7"; // Forest-Tint
        else if (primary && DIAG[primary] === slug) roleBg = "#FEE2E2"; // Crimson-Tint
        return (
          <div
            key={slug}
            className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center px-2 py-1 rounded-lg"
            style={{
              left: `${x}%`,
              top: `${y}%`,
              opacity: isPrimary ? 1 : 0.85,
              backgroundColor: roleBg,
            }}
            data-testid={`matrix-quadrant-${slug}`}
            data-role={isPrimary ? "primary" : (primary && DIAG[primary] === slug ? "gegenueber" : "nachbarn")}
          >
            <Glyph size={36} />
            <span className="mt-1 text-[10px] font-semibold text-[var(--fts-text-muted)]">{label}</span>
          </div>
        );
      })}

      {/* User-Position als Punkt mit Glow */}
      <div
        className="absolute -translate-x-1/2 -translate-y-1/2 transition-all duration-700"
        style={{ left: `${px}%`, top: `${py}%` }}
        data-testid="matrix-user-position"
      >
        <div className="relative">
          <span className="absolute inset-0 -m-2 rounded-full bg-[var(--fts-primary-bright)] opacity-30 animate-ping" />
          <span className="relative block w-4 h-4 rounded-full bg-[var(--fts-primary-bright)] shadow-lg ring-4 ring-white">
            {PrimaryGlyph && <span className="sr-only">{NAMES[primary]}</span>}
          </span>
        </div>
      </div>
    </div>
  );
}

function PrimarySection({ primary, isMischtyp, secondary }) {
  const Glyph = GLYPH[primary];
  const t = PRIMARY_TEXT[primary];
  return (
    <section className="rounded-2xl bg-white dark:bg-slate-900 border border-[var(--fts-neutral-200)] p-8" data-testid="result-primary-section">
      <div className="flex items-start gap-6 flex-col sm:flex-row">
        <Glyph size={88} />
        <div className="flex-1">
          <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">
            Dein dominierender Fokustyp
          </p>
          <h1 className="mt-2 text-4xl sm:text-5xl font-extrabold tracking-tight text-[var(--fts-text-primary)]" data-testid="result-primary-name">
            Dein dominierender Fokustyp ist Typ {NAMES[primary]}.
          </h1>
          {isMischtyp && (
            <p className="mt-2 text-sm font-semibold text-[var(--fts-accent)]" data-testid="result-mischtyp-hint">
              {secondary
                ? `Mischtyp: Stark in ${NAMES[primary]} mit ausgeprägter zweiter Seite ${NAMES[secondary]}.`
                : `Mischtyp: Mehrere Fokustypen liegen dicht beieinander — du tickst situationsabhängig.`}
            </p>
          )}
          <p className="mt-4 text-base text-slate-700 dark:text-slate-200 leading-relaxed">{t.intro}</p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <h3 className="text-sm font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">Was dich auszeichnet</h3>
          <ul className="mt-2 space-y-2 text-sm text-slate-700 dark:text-slate-200">
            {t.auszeichnet.map((it, i) => (
              <li key={i} className="flex gap-2"><span className="text-[var(--fts-primary-bright)] font-bold">•</span>{it}</li>
            ))}
          </ul>
        </div>
        <div>
          <h3 className="text-sm font-bold uppercase tracking-widest text-[var(--fts-accent)]">Was dich herausfordert</h3>
          <ul className="mt-2 space-y-2 text-sm text-slate-700 dark:text-slate-200">
            {t.fordert.map((it, i) => (
              <li key={i} className="flex gap-2"><span className="text-[var(--fts-accent)] font-bold">•</span>{it}</li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function SecondarySection({ secondary, navigate }) {
  if (!secondary) return null;
  const Glyph = GLYPH[secondary];
  return (
    <section className="rounded-2xl bg-white dark:bg-slate-900 border border-[var(--fts-neutral-200)] p-7" data-testid="result-secondary-section">
      <div className="flex items-start gap-4 flex-col sm:flex-row">
        <Glyph size={56} />
        <div className="flex-1">
          <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-text-muted)]">Sekundärer Fokustyp</p>
          <h3 className="mt-1 text-xl font-bold text-[var(--fts-text-primary)]">
            Du hast auch eine starke Seite als Fokustyp {NAMES[secondary]}.
          </h3>
          <p className="mt-2 text-sm text-[var(--fts-text-muted)] leading-relaxed">
            Diese zweite Seite zeigt sich, wenn du in einem anderen Kontext bist. Lerne sie kennen — sie ergänzt dich.
          </p>
          <button
            onClick={() => navigate(`/dashboard/fokustyp/${secondary}`)}
            data-testid="result-secondary-cta"
            className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[var(--fts-primary-bright)] hover:underline"
          >
            Zum Steckbrief {NAMES[secondary]} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
}

function WFragenSection({ result }) {
  const W_FRAGE_LABEL = { was: "WAS", wie: "WIE", warum: "WARUM", wer: "WER" };
  const label = W_FRAGE_LABEL[result.primary_w_frage] || (result.primary_w_frage || "").toUpperCase();
  return (
    <section className="rounded-2xl bg-white dark:bg-slate-900 border border-[var(--fts-neutral-200)] p-7" data-testid="result-wfragen-section">
      <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">
        Deine W-Frage
      </p>
      <h3 className="mt-1 text-xl font-bold text-[var(--fts-text-primary)]">
        Du fragst zuerst: <span className="text-[var(--fts-primary-bright)]">{label}?</span>
      </h3>
      <p className="mt-3 text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
        {result.rationale}
      </p>
      <div className="mt-4 flex flex-wrap gap-2">
        <span
          className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)] bg-blue-50 border border-blue-100 rounded-full px-2.5 py-1"
          data-testid="result-w-frage-badge"
        >
          {label}
        </span>
        <span className="text-xs font-bold uppercase tracking-widest text-[var(--fts-text-muted)] bg-slate-100 rounded-full px-2.5 py-1">
          {result.dimension}
        </span>
      </div>
    </section>
  );
}

function MagicTippsSection({ primary }) {
  const tipps = MAGIC_TIPPS[primary] || [];
  return (
    <section className="rounded-2xl bg-white dark:bg-slate-900 border border-[var(--fts-neutral-200)] p-7" data-testid="result-magic-tipps-section">
      <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">3 Sofort-Tipps für dich</p>
      <ol className="mt-4 space-y-3 text-sm text-slate-700 dark:text-slate-200">
        {tipps.map((t, i) => (
          <li key={i} className="flex gap-3" data-testid={`result-magic-tipp-${i + 1}`}>
            <span className="w-7 h-7 flex-shrink-0 rounded-full bg-[var(--fts-primary-bright)] text-white font-bold text-xs flex items-center justify-center">
              {i + 1}
            </span>
            <span className="leading-relaxed pt-0.5">{t}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

function FinalSection({ user, result, answers, onSaved, navigate, retryStatus }) {
  const copy = pickProSoloCopy(user);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [overwrite, setOverwrite] = useState(false);

  const isPaid = user?.is_premium;
  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post("/quiz/detail/save", {
        answers,
        overwrite_primary_fokustyp: overwrite,
      });
      setSaved(true);
      toast.success("Ergebnis gespeichert.");
      sessionStorage.removeItem("fts_detail_test_result");
      onSaved?.();
    } catch (e) {
      toast.error("Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  // Sprint M5.39 (30.07.2026) — Block A1: Retry-Mimik.
  // Anzeige-Logik basierend auf attempts:
  //   attempts=1 → Hinweis „Letzte Moeglichkeit, sofort wiederholen"
  //   attempts=2 → 24h-Sperre bis Versuch 3
  //   attempts>=3 → Fokustyp endgueltig fixiert (keine Retry mehr)
  const attempts = retryStatus?.attempts ?? 0;
  const isFixed = retryStatus?.fixed || attempts >= 3;
  const needsWait24h = retryStatus?.needs_24h_wait;

  return (
    <div className="space-y-5">
      {/* Retry-Hinweis: Nach 1. Versuch — sofort wiederholen moeglich */}
      {attempts === 1 && !isFixed && (
        <section
          data-testid="quickcheck-retry-notice-attempt-2"
          className="rounded-2xl border-2 border-[#4563BB] bg-blue-50 p-6"
        >
          <p className="text-xs font-bold uppercase tracking-widest text-[#4563BB]">
            Retry · Versuch 2 von 3
          </p>
          <h3 className="mt-1 text-xl font-extrabold text-slate-900 dark:text-white">
            Du bekommst jetzt eine letzte Möglichkeit, den Test zu wiederholen.
          </h3>
          <p className="mt-2 text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
            Stell dir die Situation vor, antworte wirklich so, wie du es glaubst. Das Ergebnis
            bildet die Grundlage für dein Profil — danach ist der Fokustyp fixiert.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => {
                sessionStorage.removeItem("fts_detail_test_result");
                navigate("/dashboard/test");
              }}
              data-testid="quickcheck-retry-btn-attempt-2"
              className="inline-flex items-center gap-2 rounded-xl bg-[#4563BB] hover:opacity-90 text-white font-bold px-5 py-3 text-sm"
            >
              <RotateCw className="w-4 h-4" /> Test wiederholen
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || saved}
              data-testid="quickcheck-retry-keep-btn"
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-[#4563BB] text-slate-700 dark:text-slate-200 font-semibold px-5 py-3 text-sm"
            >
              Ergebnis behalten
            </button>
          </div>
        </section>
      )}

      {/* Retry-Hinweis: Nach 2. Versuch — 24h-Sperre, dann 3. Versuch */}
      {attempts === 2 && !isFixed && needsWait24h && (
        <section
          data-testid="quickcheck-retry-notice-locked-24h"
          className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 p-6"
        >
          <p className="text-xs font-bold uppercase tracking-widest text-slate-600 dark:text-slate-400">
            Dein Fokustyp ist vorläufig fixiert
          </p>
          <h3 className="mt-1 text-xl font-extrabold text-slate-900 dark:text-white">
            Dein dominierender Fokustyp ist Typ {NAMES[result.primary_fokustyp]}.
          </h3>
          <p className="mt-2 text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
            Du kannst deinen Fokustyp in 24 Stunden nochmal verifizieren. Bis dahin ist er die
            Grundlage für deine Videos und deine Fokustypen-Bibliothek.
          </p>
        </section>
      )}

      {/* Retry-Hinweis: 24h vorbei, 3. Versuch moeglich */}
      {attempts === 2 && !isFixed && !needsWait24h && (
        <section
          data-testid="quickcheck-retry-notice-attempt-3"
          className="rounded-2xl border-2 border-[#4563BB] bg-blue-50 p-6"
        >
          <p className="text-xs font-bold uppercase tracking-widest text-[#4563BB]">
            Retry · Versuch 3 von 3 · letzte Möglichkeit
          </p>
          <h3 className="mt-1 text-xl font-extrabold text-slate-900 dark:text-white">
            Du bist jetzt 24 Stunden dabei. Möchtest du deinen Fokustyp nochmal verifizieren?
          </h3>
          <p className="mt-2 text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
            Du erstellst quasi dein Profil — das ist Grundlage für alles Weitere. Danach ist der
            Fokustyp endgültig fixiert.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => {
                sessionStorage.removeItem("fts_detail_test_result");
                navigate("/dashboard/test");
              }}
              data-testid="quickcheck-retry-btn-attempt-3"
              className="inline-flex items-center gap-2 rounded-xl bg-[#4563BB] hover:opacity-90 text-white font-bold px-5 py-3 text-sm"
            >
              <RotateCw className="w-4 h-4" /> Ja, verifizieren
            </button>
          </div>
        </section>
      )}

      {/* Endgueltig fixiert nach 3. Versuch */}
      {isFixed && (
        <section
          data-testid="quickcheck-retry-notice-fixed"
          className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6"
        >
          <p className="text-xs font-bold uppercase tracking-widest text-emerald-700">
            Fokustyp endgültig fixiert
          </p>
          <h3 className="mt-1 text-xl font-extrabold text-slate-900 dark:text-white">
            Dein dominierender Fokustyp ist Typ {NAMES[result.primary_fokustyp]} — jetzt endgültig fixiert.
          </h3>
          <p className="mt-2 text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
            Das ist die Grundlage für dein Profil. Du kannst den Fokustyp nicht mehr ändern.
          </p>
        </section>
      )}

      {/* Sprint V38 (So 15.09.2026) — Share-CTA direkt nach Ergebnis
          (Peak-Emotion-Timing) mit Referral-Code-Kopplung. */}
      {result?.primary_fokustyp && (
        <section
          className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-gradient-to-br from-white to-slate-50 dark:from-slate-900 dark:to-slate-950 p-6"
          data-testid="result-share-section"
        >
          <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">
            Teilen — und andere mitnehmen
          </p>
          <h3 className="mt-1 text-lg font-bold text-slate-900 dark:text-white">
            Wer soll wissen, wie du tickst?
          </h3>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300 leading-relaxed max-w-xl">
            Schick deinen Fokustyp an Menschen, für die sich die Frage lohnt — Team, Partner, Kunden. Sie machen den Test in 2 Minuten und ihr habt sofort einen gemeinsamen Sprachraum.
          </p>
          <div className="mt-4">
            <ShareFokustypButtonV2
              fokustyp={result.primary_fokustyp}
              referralCode={user?.referral_code || null}
              context="quickcheck_result"
              testId="result-share-btn"
            />
          </div>
        </section>
      )}

      {/* Sprint 7.4 P0 Persistenz-Fix (27.06.2026) — Free-User können ihr Ergebnis
          jetzt AUCH speichern. Save-Section ist immer sichtbar; Cross-Sell für
          Free-User wird ZUSÄTZLICH angezeigt, ersetzt aber nicht mehr Save. */}
      <section className="rounded-2xl bg-white dark:bg-slate-900 border border-[var(--fts-neutral-200)] p-7" data-testid="result-save-section">
        <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">Ergebnis sichern</p>
        <h3 className="mt-1 text-xl font-bold text-[var(--fts-text-primary)]">
          Möchtest du dieses Ergebnis speichern?
        </h3>
        <p className="mt-2 text-sm text-[var(--fts-text-muted)] leading-relaxed">
          So findest du es jederzeit im Mitgliederbereich wieder. Dein Primärfokustyp wird nur überschrieben, wenn du es explizit erlaubst.
        </p>
        {user?.primary_fokustyp && user.primary_fokustyp !== result.primary_fokustyp && (
          <label className="mt-4 flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
            <input
              type="checkbox"
              checked={overwrite}
              onChange={(e) => setOverwrite(e.target.checked)}
              data-testid="result-overwrite-checkbox"
              className="mt-0.5"
            />
            <span>
              Primärfokustyp überschreiben — aktuell <span className="font-semibold">{NAMES[user.primary_fokustyp]}</span>, neu <span className="font-semibold">{NAMES[result.primary_fokustyp]}</span>.
            </span>
          </label>
        )}
        <div className="mt-5 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || saved}
            data-testid="result-save-btn"
            className="inline-flex items-center gap-2 rounded-xl bg-[var(--fts-success)] hover:opacity-90 text-white font-semibold px-5 py-3 text-sm disabled:opacity-50"
          >
            {saved ? <Check className="w-4 h-4" /> : saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saved ? "Gespeichert" : "Ergebnis speichern"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/cockpit")}
            data-testid="result-skip-btn"
            className="inline-flex items-center gap-2 rounded-xl border border-[var(--fts-neutral-200)] bg-white dark:bg-slate-900 hover:border-[var(--fts-primary-bright)] text-[var(--fts-text-primary)] font-semibold px-5 py-3 text-sm"
          >
            Später im Mitgliederbereich ansehen
          </button>
        </div>
      </section>

      {!isPaid && (
        <section
          className="rounded-2xl p-8 text-white"
          style={{ backgroundColor: "var(--fts-primary-deep)" }}
          data-testid="result-cross-sell"
        >
          <div className="flex items-start gap-4 flex-col md:flex-row md:items-center">
            <Sparkles className="w-7 h-7 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-xl font-extrabold tracking-tight">Du willst alle 4 Fokustypen kennenlernen?</h3>
              <p className="mt-2 text-sm opacity-90">{copy.priceLine}</p>
              {copy.isTrialEligible && (
                <p className="mt-1 text-xs opacity-95 font-semibold" data-testid="result-abo-trial-hint">
                  Start ohne Risiko: 7 Tage für 1 €.
                </p>
              )}
            </div>
            <button
              onClick={() => navigate("/dashboard/abo")}
              data-testid="result-abo-cta"
              className="rounded-xl bg-white text-[var(--fts-primary-deep)] font-bold px-5 py-3 text-sm hover:bg-slate-50"
            >
              {copy.ctaLabel}
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

export default function DashboardTestResult() {
  const navigate = useNavigate();
  const [payload, setPayload] = useState(null);
  // Sprint M5.39 (30.07.2026) — Block A1: Quick-Check Retry-Mimik.
  const [retryStatus, setRetryStatus] = useState(null);

  useEffect(() => {
    const raw = sessionStorage.getItem("fts_detail_test_result");
    if (!raw) {
      navigate("/dashboard/test", { replace: true });
      return;
    }
    try {
      setPayload(JSON.parse(raw));
    } catch {
      navigate("/dashboard/test", { replace: true });
    }
  }, [navigate]);

  // Attempt-Counter serverseitig hochzaehlen — idempotent per sessionStorage.
  useEffect(() => {
    if (!payload) return;
    const idempotencyKey = `f4s_qc_attempt_counted_${payload?.result?.primary_fokustyp || "x"}_${payload?.answers?.length || 0}_${payload?.result?.matrix_position?.x || 0}_${payload?.result?.matrix_position?.y || 0}`;
    let alreadyCounted = false;
    try {
      alreadyCounted = sessionStorage.getItem(idempotencyKey) === "1";
    } catch (_) { /* noop */ }
    if (alreadyCounted) {
      // Nur Status holen, nicht neu zaehlen
      api.get("/quickcheck/attempt-status").then((r) => setRetryStatus(r.data)).catch(() => setRetryStatus(null));
      return;
    }
    api.post("/quickcheck/attempt-completed").then((r) => {
      try { sessionStorage.setItem(idempotencyKey, "1"); } catch (_) { /* noop */ }
      // Vollstaendigen Status holen (mit needs_24h_wait etc.)
      return api.get("/quickcheck/attempt-status");
    }).then((r) => {
      if (r?.data) setRetryStatus(r.data);
    }).catch(() => setRetryStatus(null));
  }, [payload]);

  const matrixOk = useMemo(
    () => payload?.result?.matrix_position && typeof payload.result.matrix_position.x === "number",
    [payload],
  );

  return (
    <DashboardLayout>
      {({ data }) => {
        if (!payload) {
          return (
            <div className="flex justify-center py-20">
              <Loader2 className="w-6 h-6 text-[var(--fts-primary-bright)] animate-spin" />
            </div>
          );
        }
        const { result, answers } = payload;
        const primary = result.primary_fokustyp;
        return (
          <div className="max-w-3xl mx-auto px-6 lg:px-8 py-12 space-y-6" data-testid="dashboard-test-result-page">
            {/* Section 1: Primary Fokustyp */}
            <PrimarySection primary={primary} isMischtyp={result.is_mischtyp} secondary={result.secondary_fokustyp} />

            {/* Section 2: 2x2 Matrix */}
            {matrixOk && (
              <section className="rounded-2xl bg-white dark:bg-slate-900 border border-[var(--fts-neutral-200)] p-7" data-testid="result-matrix-section">
                <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">Deine Position in der 4er-Matrix</p>
                <h3 className="mt-1 text-xl font-bold text-[var(--fts-text-primary)]">So tickst du im Verhältnis zu den anderen Fokustypen</h3>
                <p
                  className="mt-2 text-sm text-[var(--fts-text-muted)] leading-relaxed"
                  data-testid="matrix-erklaerung"
                >
                  Die Vier-Felder-Matrix zeigt, wo dein Anteil im Verhältnis zu den anderen liegt.
                  Nachbarn (z.B. Eule-Biene) teilen sich Achsen — Sachlichkeit oder Methode.
                  Entgegengesetzte (z.B. Eule-Hund) liegen weit auseinander — verschiedene Werte,
                  Erwartungen, Verhaltensweisen.
                </p>
                <div className="mt-5">
                  <MatrixVisualizer matrixPosition={result.matrix_position} primary={primary} />
                </div>
              </section>
            )}

            {/* Section 3: Secondary */}
            <SecondarySection secondary={result.secondary_fokustyp} navigate={navigate} />

            {/* Section 4: W-Fragen */}
            <WFragenSection result={result} />

            {/* Section 5: Magic Tipps */}
            <MagicTippsSection primary={primary} />

            {/* Section 6: Save / Cross-Sell + Retry-Mimik (Sprint M5.39) */}
            <FinalSection
              user={data.user}
              result={result}
              answers={answers}
              navigate={navigate}
              retryStatus={retryStatus}
            />

            {/* Bottom actions */}
            <div className="flex flex-wrap gap-3 pt-4 border-t border-[var(--fts-neutral-200)]" data-testid="result-bottom-actions">
              {!(retryStatus?.fixed) && !(retryStatus?.needs_24h_wait) && (
                <button
                  type="button"
                  onClick={() => {
                    sessionStorage.removeItem("fts_detail_test_result");
                    navigate("/dashboard/test");
                  }}
                  data-testid="result-retake-btn"
                  className="inline-flex items-center gap-1 text-sm font-semibold text-[var(--fts-text-muted)] hover:text-[var(--fts-text-primary)]"
                >
                  <RotateCw className="w-4 h-4" /> Erneut testen
                </button>
              )}
              <button
                type="button"
                onClick={() => navigate("/cockpit")}
                data-testid="result-dashboard-btn"
                className="ml-auto inline-flex items-center gap-1 text-sm font-semibold text-[var(--fts-primary-bright)] hover:underline"
              >
                Zum Mitgliederbereich <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        );
      }}
    </DashboardLayout>
  );
}
